import { useCallback, useEffect, useRef } from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import {
  ShippingInstructionsTimeOnTaskEventArgs,
  ShippingInstructionsTimeOnTaskEventExtra,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

type Props = {
  withEventListeners: boolean
  screen: string
  shipmentId: number | undefined
  trackingFunction: (args: ShippingInstructionsTimeOnTaskEventArgs) => {
    event: string
    extra: ShippingInstructionsTimeOnTaskEventExtra
  }
}

type TrackTimeOnTaskProps = {
  localScreen?: string
  skipEventListenerTracking?: boolean
}

export const useTimeOnTaskTracking = ({
  withEventListeners,
  screen,
  shipmentId,
  trackingFunction,
}: Props) => {
  const MINIMUM_TIME_ON_TASK = 1000
  const eventListenersStartDate = useRef<Date | null>(null)
  const startDate = useRef<Date | null>(null)
  const skipEventListenersTracking = useRef<boolean>(false)

  const { track } = useTracking()

  const { relativeUrl, searchParams } = useBrowserNavigation()

  useEffect(() => {
    eventListenersStartDate.current = new Date()
    startDate.current = new Date()
  }, [])

  const trackTimeOnTaskFromEventListeners = useCallback(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return

    if (skipEventListenersTracking.current) return

    if (eventListenersStartDate.current) {
      const endDate = new Date()

      if (endDate.getTime() - eventListenersStartDate.current.getTime() <= MINIMUM_TIME_ON_TASK) {
        return
      }

      track(
        trackingFunction({
          screen,
          duration: endDate.getTime() - eventListenersStartDate.current.getTime(),
          shipmentId,
        }),
      )
    }

    if (!document.hidden) {
      eventListenersStartDate.current = new Date()
      startDate.current = new Date()
    }
  }, [screen, track, trackingFunction, shipmentId])

  const trackTimeOnTask = useCallback(
    (payload?: TrackTimeOnTaskProps) => {
      if (!startDate.current) return

      if (payload?.skipEventListenerTracking) {
        skipEventListenersTracking.current = true
      }

      const endDate = new Date()

      if (endDate.getTime() - startDate.current.getTime() <= MINIMUM_TIME_ON_TASK) return

      track(
        trackingFunction({
          screen: payload?.localScreen || screen,
          duration: endDate.getTime() - startDate.current.getTime(),
          shipmentId,
        }),
      )

      startDate.current = new Date()
      eventListenersStartDate.current = new Date()
    },
    [screen, track, trackingFunction, shipmentId],
  )

  useEffect(() => {
    if (!withEventListeners) return

    trackTimeOnTaskFromEventListeners()
  }, [relativeUrl, searchParams, trackTimeOnTaskFromEventListeners, withEventListeners])

  useEffect(() => {
    if (!withEventListeners) return undefined

    document.addEventListener('visibilitychange', trackTimeOnTaskFromEventListeners)
    window.addEventListener('beforeunload', trackTimeOnTaskFromEventListeners)

    eventListenersStartDate.current = new Date()

    return () => {
      document.removeEventListener('visibilitychange', trackTimeOnTaskFromEventListeners)
      window.removeEventListener('beforeunload', trackTimeOnTaskFromEventListeners)
    }
  }, [trackTimeOnTaskFromEventListeners, withEventListeners])

  return trackTimeOnTask
}

export default useTimeOnTaskTracking
